import type { UserPublicMe } from "@common/client"
import { hasPermission } from "@common/lib/permissions"
import { z } from "zod"
import { overviewSearchFields } from "@/routes/_layout/aircraft/-lib/overview"
import { dueSearchSchema } from "@/routes/_layout/forecasts_/-lib/search"
import { adSearchFields } from "./ad-compliance"
import { fittedSearchFields } from "./fitted-components"
import { modSearchFields } from "./mod-status"
import { sbSearchFields } from "./sb-compliance"
import { techLogSearchFields } from "./tech-log"
import { workOrderSearchFields } from "./work-orders"

export const TABS = [
  "overview",
  "general",
  "ad-compliance",
  "sb-compliance",
  "fitted-components",
  "modification-status",
  "forecast",
  "tech-log",
  "work-orders",
  "customer-info",
] as const

export const tabSchema = z.enum(TABS)

export type Tab = z.infer<typeof tabSchema>

export const aircraftPageSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  ...overviewSearchFields,
  ...techLogSearchFields,
  ...fittedSearchFields,
  ...dueSearchSchema.shape,
  ...adSearchFields,
  ...sbSearchFields,
  ...modSearchFields,
  ...workOrderSearchFields,
})

export type AircraftPageSearch = z.infer<typeof aircraftPageSearchSchema>

export const DEFAULT_TAB: Tab = "general"

const TAB_PERMISSIONS: Record<Tab, string | null> = {
  overview: null,
  general: null,
  "ad-compliance": "trace_ad_eval_report:read",
  "sb-compliance": "trace_sb_eval_report:read",
  "fitted-components": "fitted_components:read",
  "modification-status": "mod_status:read",
  "tech-log": "atl_page:read",
  forecast: "forecast:read",
  "work-orders": "work_package:read",
  "customer-info": "customer:read",
}

export function tabsFor(
  user: UserPublicMe | undefined,
  overview: boolean,
  woPages: boolean,
): Tab[] {
  return TABS.filter((tab) => {
    if (tab === "overview") return overview
    if ((tab === "forecast" || tab === "tech-log") && !woPages) return false
    const permission = TAB_PERMISSIONS[tab]
    return permission === null || hasPermission(user, permission)
  })
}

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case "overview":
      return "Overview"
    case "general":
      return "General"
    case "ad-compliance":
      return "AD Compliance"
    case "sb-compliance":
      return "SB Compliance"
    case "fitted-components":
      return "Fitted components"
    case "modification-status":
      return "Modification status"
    case "tech-log":
      return "Tech log"
    case "forecast":
      return "Forecast"
    case "work-orders":
      return "Work orders"
    case "customer-info":
      return "Customer info"
  }
}
